import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

import { selectAuthData } from '@/containers/Login/AuthSelectors'

import { italyFiscalCodesManagementActions } from './italyFiscalCodesManagementSagas'
import { selectItalyFiscalCodesManagement } from './italyFiscalCodesManagementSelectors'
import {
  applySearch,
  clearItalyFiscalCodesManagement,
  openCreateModal,
  selectTable,
  setPage,
  setSearchInput,
} from './italyFiscalCodesManagementSlice'
import { FiscalCodeTable, ItalyFiscalCodesAuthData } from './types'

export const SEARCH_DEBOUNCE_MS = 600
const FISCAL_CODE_TABLES: FiscalCodeTable[] = ['cities', 'countries']

const isFiscalCodeTable = (value: string): value is FiscalCodeTable =>
  FISCAL_CODE_TABLES.includes(value as FiscalCodeTable)

const useItalyFiscalCodesManagement = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const authData: ItalyFiscalCodesAuthData | null = useSelector(selectAuthData)
  const listRoute = authData?.extraRoutes?.italyFiscalCodeLocations?.list
  const { table, searchInput, search, page, lastPage, total, isLoading, loadError } = useSelector(
    selectItalyFiscalCodesManagement,
  )

  const onTableChange = (value: string) => {
    if (value === table || !isFiscalCodeTable(value)) return
    dispatch(selectTable(value))
  }

  const onSearchChange = (value: string | null) => {
    dispatch(setSearchInput(value ?? ''))
  }

  const onAdd = () => {
    dispatch(openCreateModal())
  }

  const onPreviousPage = () => {
    dispatch(setPage(page - 1))
  }

  const onNextPage = () => {
    dispatch(setPage(page + 1))
  }

  useEffect(() => {
    return () => {
      dispatch(clearItalyFiscalCodesManagement())
    }
  }, [])

  useEffect(() => {
    if (searchInput === search) return
    const timer = setTimeout(() => dispatch(applySearch(searchInput)), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [searchInput, search])

  useEffect(() => {
    if (!listRoute) return
    dispatch({ type: italyFiscalCodesManagementActions.LOAD })
  }, [listRoute, table, search, page])

  const error = listRoute ? loadError : t('italyFiscalCodesManagement:relogin')

  return {
    title: t('italyFiscalCodesManagement:title'),
    resultsCount: t('italyFiscalCodesManagement:results_count', { count: total }),
    tableOptions: FISCAL_CODE_TABLES.map(option => ({
      [option]: t(`italyFiscalCodesManagement:tables.${option}`),
    })),
    table,
    searchInput,
    searchLabel: t('italyFiscalCodesManagement:search'),
    addLabel: t('italyFiscalCodesManagement:add_button'),
    loadingText: t('common:fetching_data'),
    previousLabel: t('common:previous'),
    nextLabel: t('common:next'),
    error,
    isErrorHidden: error === '',
    isLoaderHidden: !isLoading,
    isTableHidden: isLoading || error !== '',
    pageLabel: t('italyFiscalCodesManagement:pagination', { page, lastPage }),
    isPreviousDisabled: page <= 1 || isLoading,
    isNextDisabled: page >= lastPage || isLoading,
    onTableChange,
    onSearchChange,
    onAdd,
    onPreviousPage,
    onNextPage,
  }
}

export default useItalyFiscalCodesManagement
